import { InjectionToken } from '@angular/core';

import { Observable } from 'rxjs';

import { FulfilmentMethod } from '@core/models/listing.enum';
import { CheckoutSessionState, OrderState, ShippingRateState } from '@core/models/order.state';
import { PaginatedResult } from '@core/models/paginated.response';

/** Contact + address captured at checkout step 1. */
export interface CheckoutContactRequest {
  fullName: string;
  email: string;
  addressLine: string;
}

/** Per-group fulfilment choices made at checkout step 1. */
export interface PlaceOrderGroupRequest {
  tenantId: number;
  storeId: number;
  fulfilment: FulfilmentMethod;
  /** Chosen carrier service (SHP-01); null on pickup groups. */
  shippingRateCode: string | null;
}

export interface PlaceOrderRequest {
  sessionId: number;
  contact: CheckoutContactRequest;
  groups: PlaceOrderGroupRequest[];
  shopperId: number | null;
}

/**
 * Checkout + order contract (HLD §8, §17). `startCheckout` creates the
 * 10-minute reservation over the caller's cart (D-04); `placeOrder` consumes
 * it and mints the order with pickup tickets per pickup group.
 */
export interface OrderRepository {
  startCheckout(ownerKey: string): Observable<CheckoutSessionState>;
  /**
   * Rate-shop a store's shipping options for a given items subtotal (SHP-01).
   * The subtotal matters because the retailer's free-shipping threshold can
   * zero the cheapest service.
   */
  getShippingRates(storeId: number, itemsSubtotal: number): Observable<ShippingRateState[]>;
  /** Re-read an active session (refresh rejoin); null when gone/expired. */
  getCheckoutSession(sessionId: number): Observable<CheckoutSessionState | null>;
  releaseCheckout(sessionId: number): Observable<void>;
  placeOrder(request: PlaceOrderRequest): Observable<OrderState>;
  listOrders(shopperId: number, pageNumber: number, pageSize: number): Observable<PaginatedResult<OrderState>>;
  getOrder(orderRef: string): Observable<OrderState | null>;
  /** Guest order lookup (BUY-02): ref + checkout email, both case-insensitive. */
  findByRefAndEmail(orderRef: string, email: string): Observable<OrderState | null>;
}

export const ORDER_REPOSITORY = new InjectionToken<OrderRepository>('ORDER_REPOSITORY');
